import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  Check,
  Database,
  KeyRound,
  Loader2,
  Mail,
  RefreshCw,
  AlertTriangle,
  XCircle,
  Boxes,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * AdminSystemCheck, Verificação Geral do Sistema (Área 2).
 * Cards semafóricos: 🟢 ok / 🟡 warn / 🔴 fail.
 */

const ICON = {
  mongo: Database,
  llm_key: KeyRound,
  resend: Mail,
  collections: Boxes,
  scheduler: Clock,
  jobs: Activity,
};
const LABEL = {
  mongo: "MongoDB",
  llm_key: "Emergent LLM Key",
  resend: "E-mails (Resend)",
  collections: "Coleções críticas",
  scheduler: "Agendador (cron)",
  jobs: "Jobs recentes",
};

const STATUS_COLOR = {
  ok: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  warn: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", dot: "bg-amber-500" },
  fail: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
};
const STATUS_LABEL = { ok: "Saudável", warn: "Atenção", fail: "Crítico" };

export default function AdminSystemCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    api
      .get("/admin/system/check")
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Falha ao consultar."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const overall = data?.overall || "ok";
  const c = STATUS_COLOR[overall] || STATUS_COLOR.ok;

  return (
    <section
      data-testid="admin-system-check-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#C5A059] mb-1">
            <Activity size={12} strokeWidth={1.6} />
            <span className="text-[0.6rem] uppercase tracking-[0.28em] font-medium">
              Verificação do sistema
            </span>
          </div>
          <h2 className="font-serif-display text-[1.4rem] text-[#0B1E36] tracking-[-0.01em]">
            Status geral
          </h2>
          {data?.checked_at && (
            <p className="text-[0.72rem] text-[#0B1E36]/45 mt-1 tabular-nums">
              Última verificação: {new Date(data.checked_at).toLocaleString("pt-BR")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="admin-system-overall"
            className={`inline-flex items-center gap-2 ${c.bg} ${c.border} ${c.text} border px-4 py-2`}
          >
            <span className={`w-2 h-2 rounded-full ${c.dot}`} />
            <span className="text-[0.7rem] uppercase tracking-[0.22em] font-medium">
              {STATUS_LABEL[overall]}
            </span>
          </div>
          <button
            data-testid="admin-system-refresh"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.22em] text-[#0B1E36] bg-[#FAF8F4] border border-[#0B1E36]/15 hover:bg-white px-3 py-2"
          >
            <RefreshCw size={13} strokeWidth={1.7} className={loading ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 mb-4 text-sm">
          {err}
        </div>
      )}

      {!data ? (
        <div className="text-center py-12 text-[#0B1E36]/45">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(data.checks).map(([key, val]) => (
            <CheckCard key={key} keyName={key} val={val} />
          ))}
        </div>
      )}
    </section>
  );
}

function CheckCard({ keyName, val }) {
  const Icon = ICON[keyName] || Activity;
  const c = STATUS_COLOR[val.status] || STATUS_COLOR.ok;
  return (
    <div
      data-testid={`admin-system-check-${keyName}`}
      className={`border ${c.border} ${c.bg} p-4`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[#0B1E36]">
          <Icon size={14} strokeWidth={1.7} />
          <span className="text-[0.7rem] uppercase tracking-[0.22em] font-medium">
            {LABEL[keyName] || keyName}
          </span>
        </div>
        <StatusBadge status={val.status} />
      </div>
      {val.message && (
        <p className="text-[0.82rem] text-[#0B1E36]/75 leading-[1.5]">{val.message}</p>
      )}
      {keyName === "collections" && val.collections && (
        <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[0.72rem]">
          {val.collections.map((c) => (
            <li
              key={c.name}
              data-testid={`admin-system-coll-${c.name}`}
              className="flex items-center justify-between"
            >
              <span className="text-[#0B1E36]/65">{c.name}</span>
              <span className={`tabular-nums font-medium ${
                c.status === "fail" ? "text-rose-700" :
                c.status === "warn" ? "text-amber-700" : "text-[#0B1E36]"
              }`}>
                {c.count}
              </span>
            </li>
          ))}
        </ul>
      )}
      {keyName === "scheduler" && val.jobs?.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[0.7rem] text-[#0B1E36]/55">
          {val.jobs.map((j) => (
            <li key={j.id} className="truncate">
              <span className="text-[#0B1E36]/70">{j.id}</span>
              {j.next_run && (
                <> · próx.: {new Date(j.next_run).toLocaleString("pt-BR")}</>
              )}
            </li>
          ))}
        </ul>
      )}
      {keyName === "jobs" && (
        <div className="mt-2 grid grid-cols-2 gap-2 text-[0.7rem] text-[#0B1E36]/65">
          {val.sermon_jobs && (
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0B1E36]/45">Sermões 24h</div>
              <div className="text-[#0B1E36] tabular-nums">
                ✓ {val.sermon_jobs.done_24h || 0} · ✗ {val.sermon_jobs.failed_24h || 0}
              </div>
            </div>
          )}
          {val.plan_dispatches && (
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-[#0B1E36]/45">Disparos planos</div>
              <div className="text-[#0B1E36] tabular-nums">{val.plan_dispatches.total || 0}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.ok;
  const Icon = status === "fail" ? XCircle : status === "warn" ? AlertTriangle : Check;
  return (
    <span className={`inline-flex items-center gap-1 ${c.text} text-[0.62rem] uppercase tracking-[0.2em] font-medium`}>
      <Icon size={11} strokeWidth={1.8} />
      {STATUS_LABEL[status] || status}
    </span>
  );
}
